import React from 'react';
import { Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';

const DIM = 0.8;

const WGSL_GRADIENT_DIM = `linear-gradient(45deg, rgba(167,139,250,${DIM}), rgba(96,165,250,${DIM}))`;
const WGSL_GRADIENT_FULL = 'linear-gradient(45deg, #A78BFA, #60A5FA)';

export type TagChipProps = {
  dim?: boolean;
};

export type LangChipProps = TagChipProps;

type BaseChipProps = TagChipProps & {
  label: string;
  bg?: string;
  fg?: string;
  outlined?: boolean;
  gradient?: boolean;
};

const BaseChip: React.FC<BaseChipProps> = ({
  label,
  bg,
  fg,
  outlined,
  gradient,
  dim = false,
}) => {
  const textFull = fg || (outlined ? '#BBB' : '#111');
  const textDim = alpha(textFull, DIM);

  const sxCommon = {
    fontWeight: 600,
    height: 22,
    transition:
      'background 140ms ease, color 140ms ease, opacity 140ms ease, border-color 140ms ease',
  } as const;

  const base = outlined
    ? {
        ...sxCommon,
        color: textFull,
        borderColor: alpha('#FFF', 0.24),
        backgroundColor: 'transparent',
        opacity: dim ? DIM : 1,
        '&:hover, &:focus-visible': { opacity: 1 },
      }
    : gradient
      ? {
          ...sxCommon,
          color: dim ? alpha('#FFF', DIM) : '#FFF',
          background: dim ? WGSL_GRADIENT_DIM : WGSL_GRADIENT_FULL,
          border: '1px solid rgba(255,255,255,0.06)',
          '&:hover, &:focus-visible': {
            background: WGSL_GRADIENT_FULL,
            color: '#FFF',
          },
        }
      : {
          ...sxCommon,
          color: dim ? textDim : textFull,
          backgroundColor: bg
            ? dim
              ? alpha(bg, DIM)
              : bg
            : alpha('#FFFFFF', dim ? 0.12 : 0.16),
          border: '1px solid rgba(255,255,255,0.06)',
          '&:hover, &:focus-visible': {
            backgroundColor: bg || '#FFF',
            color: textFull,
          },
        };

  return (
    <Chip
      label={label}
      size="small"
      variant={outlined ? 'outlined' : 'filled'}
      sx={base}
    />
  );
};

export const Rust: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="Rust" bg="#DEA584" fg="#1A1208" dim={dim} />
);

export const Python: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="Python" bg="#3572A5" fg="#FFF" dim={dim} />
);

export const WGSL: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="WGSL" gradient dim={dim} />
);

export const LLVM: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="LLVM" bg="#0c5369" fg="#FFF" dim={dim} />
);

export const Bevy: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="Bevy" bg="#1e1e22" fg="#FFF" outlined dim={dim} />
);

export const Simulation: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip
    label="Simulation"
    bg="#864ab8"
    fg="#FFF"
    dim={dim}
  />
);

export const Genetics: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip
    label="Genetics"
    bg="#18af4f"
    fg="#000000"
    dim={dim}
  />
);

export const FFI: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="FFI" bg="#0c5369" fg="#FFFFFF" dim={dim} />
);

export const Others: React.FC<TagChipProps> = ({ dim }) => (
  <BaseChip label="Others" outlined dim={dim} />
);

export const UnknownTag: React.FC<{ title: string } & TagChipProps> = ({
  title,
  dim,
}) => <BaseChip label={title} bg="#000000" fg="#FFF" dim={dim} />;

export const tagComponentMap: Record<string, React.FC<TagChipProps>> = {
  Rust,
  Python,
  WGSL,
  LLVM,
  Bevy,
  Simulation,
  Genetics,
  FFI,
};

export const languageComponentMap = tagComponentMap;

export type TagPaint = { kind: 'color' | 'gradient'; value: string };

export type LangPaint = TagPaint;

export function getTagPaint(tag: string): TagPaint {
  if (tag === 'WGSL')
    return {
      kind: 'gradient',
      value: 'linear-gradient(90deg, #A78BFA, #60A5FA)',
    };
  const colorMap: Record<string, string> = {
    Rust: '#DEA584',
    Python: '#3572A5',
    LLVM: '#0c5369',
    Bevy: '#1e1e22',
    Simulation: '#864ab8',
    Genomics: '#18af4f',
    FFI: '#0c5369',
    Others: alpha('#FFFFFF', 0.18),
  };
  const c = colorMap[tag] ?? colorMap.Others;
  return { kind: 'color', value: c };
}
